// 099 D07 项目详情：输入资料 / 任务历史 / Agent 步骤 / PPT 只读预览与签名下载
// 进入本页即被服务端记录审计（view_project_detail）；下载再记一条（download_pptx）。
import { Fragment, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SlideCanvas from "../../components/SlideCanvas";
import { ops } from "../../lib/api";
import { useOpsQuery } from "./useOpsQuery";
import { Note, Sect, StatusTag, fmtMoney, fmtMs, fmtTime, fmtTokens } from "./widgets";

/** 表单键的中文标签（NewProject 表单字段；未知键原样展示） */
const FORM_LABELS: Record<string, string> = {
  goal: "目标", target: "人群", budget: "预算", industry: "行业",
  product: "产品", audience: "人群", duration: "周期", note: "备注",
};

function JobSteps(props: { jobId: string }) {
  const q = useOpsQuery(() => ops.job(props.jobId), [props.jobId]);
  if (q.loading && !q.data) return <p className="muted small">步骤加载中…</p>;
  if (q.error || q.forbidden) return <p className="small" style={{ color: "var(--err)" }}>{q.error ?? q.forbidden}</p>;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div style={{ marginTop: 8 }}>
      {d.running && (
        <p className="small" style={{ color: "var(--warn)" }}>
          ● 运行中：当前步骤 <b>{d.running.currentStep}</b> · 最近心跳 {fmtTime(d.running.lastHeartbeat)}
          {d.running.stuckMs > 6 * 60_000 ? `（已 ${Math.floor(d.running.stuckMs / 60000)} 分钟无进展）` : ""}
        </p>
      )}
      <div className="d-tblwrap">
        <table>
          <thead><tr><th>步骤</th><th>Agent</th><th>状态</th><th className="num">耗时</th><th className="num">Token 入/出</th><th>错误（已脱敏）</th></tr></thead>
          <tbody>
            {d.steps.map((s, i) => (
              <tr key={`${s.step}-${i}`}>
                <td>{s.step}</td>
                <td>{s.agent}</td>
                <td>
                  {s.status === "done" ? <span className="tag t-green">done</span>
                    : s.status === "failed" ? <span className="tag t-red">failed</span>
                    : s.status === "running" ? <span className="tag t-yellow">running</span>
                    : <span className="tag t-gray">{s.status}</span>}
                </td>
                <td className="num">{s.duration_ms !== null ? fmtMs(s.duration_ms) : "—"}</td>
                <td className="num">{s.tokens_in ?? 0}/{s.tokens_out ?? 0}</td>
                <td className="small muted">{s.error || "—"}</td>
              </tr>
            ))}
            {!d.steps.length && <tr><td colSpan={6} className="muted">无步骤记录</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const q = useOpsQuery(() => ops.project(id!), [id]);
  const [openJob, setOpenJob] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [dlErr, setDlErr] = useState<string | null>(null);

  if (q.loading && !q.data) return <p className="muted">加载中…</p>;
  if (q.forbidden) return <p>🔒 {q.forbidden}</p>;
  if (q.error) return <p style={{ color: "var(--err)" }}>加载失败：{q.error}</p>;
  if (!q.data) return null;
  const d = q.data;

  const formEntries = Object.entries(d.project.form ?? {}).filter(([, v]) => v !== null && v !== undefined && String(v) !== "");

  return (
    <>
      <p className="small"><Link to="/dashboard/projects">← 返回项目列表</Link></p>
      <h2 className="d-title">{d.project.name}</h2>
      <p className="d-sub2">
        所有者：{d.owner.displayName ?? d.owner.email ?? d.owner.id.slice(0, 8)}
        {d.owner.email && d.owner.displayName ? `（${d.owner.email}）` : ""}
        {" ｜ "}行业：{d.project.industry ?? "—"} ｜ 创建于 {fmtTime(d.project.createdAt)}
        <span className="d-tagb">D07 只读 · 已审计</span>
      </p>

      <div className="d-detail">
        <div className="d-panel">
          <h4>输入资料与表单</h4>
          {formEntries.length ? (
            <ul>
              {formEntries.map(([k, v]) => (
                <li key={k}><b>{FORM_LABELS[k] ?? k}</b>：{typeof v === "object" ? JSON.stringify(v) : String(v)}</li>
              ))}
            </ul>
          ) : <p className="muted small">表单为空</p>}
        </div>
        <div className="d-panel">
          <h4>附件清单（{d.assets.length}）</h4>
          {d.assets.length ? (
            <ul>
              {d.assets.map((a) => (
                <li key={a.id}>
                  {a.filename ?? a.id.slice(0, 8)} <span className="tag t-blue">{a.kind}</span>{" "}
                  <span className="tag t-gray">提取状态未记录</span>
                  <span className="small muted"> · {fmtTime(a.createdAt)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="muted small">无附件</p>}
          <Note>附件正文不直接展示；提取成功/失败状态当前未单独落库（intake 在任务内解析），如实标注「未记录」。</Note>
        </div>
      </div>

      <Sect title={`任务历史（同一项目 ${d.jobs.length} 次生成）`} tag="D06" hint="点击行展开 Agent 步骤" />
      <div className="d-tblwrap">
        <table>
          <thead>
            <tr>
              <th>任务</th><th>状态</th><th className="num">进度</th><th className="num">耗时</th>
              <th className="num">Token</th><th className="num">估算成本</th><th>发起时间</th><th></th>
            </tr>
          </thead>
          <tbody>
            {d.jobs.map((j) => (
              <Fragment key={j.id}>
                <tr className="hoverable" onClick={() => setOpenJob(openJob === j.id ? null : j.id)}>
                  <td className="num">{j.id.slice(0, 8)}</td>
                  <td><StatusTag status={j.status} quality={j.qualityStatus} degraded={j.degraded} /></td>
                  <td className="num">{j.progress}%</td>
                  <td className="num">{j.finishedAt ? fmtMs(Date.parse(j.finishedAt) - Date.parse(j.createdAt)) : "—"}</td>
                  <td className="num">{fmtTokens(j.tokensTotal)}</td>
                  <td className="num">{fmtMoney(j.costCents)}</td>
                  <td className="small">{fmtTime(j.createdAt)}</td>
                  <td>{openJob === j.id ? "▴" : "▾"}</td>
                </tr>
                {openJob === j.id && (
                  <tr><td colSpan={8} style={{ background: "#fdfcf8" }}><JobSteps jobId={j.id} /></td></tr>
                )}
              </Fragment>
            ))}
            {!d.jobs.length && <tr><td colSpan={8} className="muted">本项目尚未发起生成</td></tr>}
          </tbody>
        </table>
      </div>

      <Sect title="PPT 版本与只读预览" tag="D07" hint="下载经服务端短期签名地址，15 分钟有效" />
      <div className="d-tblwrap" style={{ marginBottom: 12 }}>
        <table>
          <thead><tr><th>版本</th><th>标题</th><th>生成时间</th><th>PPTX</th><th></th></tr></thead>
          <tbody>
            {d.decks.map((deck) => (
              <tr key={deck.id}>
                <td className="num">{deck.id.slice(0, 8)}</td>
                <td>{deck.title}</td>
                <td className="small">{fmtTime(deck.createdAt)}</td>
                <td>{deck.hasPptx ? <span className="tag t-green">有文件</span> : <span className="tag t-gray">无文件</span>}</td>
                <td>
                  <button
                    className="d-chip"
                    disabled={!deck.hasPptx || downloading === deck.id}
                    onClick={() => {
                      setDownloading(deck.id);
                      setDlErr(null);
                      ops.projectDeckDownload(d.project.id, deck.id)
                        .then((r) => {
                          const a = document.createElement("a");
                          a.href = r.url;
                          a.download = r.name;
                          a.rel = "noopener";
                          a.click();
                        })
                        .catch((e) => setDlErr((e as Error).message))
                        .finally(() => setDownloading(null));
                    }}
                  >
                    {downloading === deck.id ? "签名中…" : "⬇ 下载 PPTX"}
                  </button>
                </td>
              </tr>
            ))}
            {!d.decks.length && <tr><td colSpan={5} className="muted">尚无生成版本</td></tr>}
          </tbody>
        </table>
      </div>
      {dlErr ? <p className="small" style={{ color: "var(--err)" }}>下载失败：{dlErr}</p> : null}

      {d.preview && d.preview.slides.length > 0 && (
        <div className="d-panel">
          <h4>最新版本预览：{d.preview.title}（{d.preview.slides.length} 页）</h4>
          <div className="d-slides">
            {d.preview.slides.map((s) => (
              <div className="d-slidecell" key={s.idx}>
                <SlideCanvas slide={s.content} pageIdx={s.idx + 1} totalPages={d.preview!.slides.length} />
                <div className="cap">第 {s.idx + 1} 页 · {s.layout}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Note>预览复用与用户侧相同的渲染组件（只读）；运营查看不修改用户任何数据。本页查看与下载均已写入运营审计日志。</Note>
    </>
  );
}
